import { useGLTF } from '@react-three/drei'
import { useFrame, useLoader } from '@react-three/fiber'
import { useMemo, useRef, useState } from 'react'
import { Vector2 } from 'three'
import { Vector4 } from 'three'
import { ShaderMaterial } from 'three'
import { TextureLoader } from 'three'

function CharTrail({ setMaterial, bikeColor, ...props }) {
  const { nodes } = useGLTF('/trail_blue.glb')
  const trailRef = useRef()
  const tex0 = useLoader(TextureLoader, '/stars.jpg')
  const tex1 = useLoader(TextureLoader, '/character.png')

  // Create an array to store the original vertex positions
  const material = useMemo(() => {
    const materials = new ShaderMaterial({
      uniforms: {
        iTime: {
          type: 'f',
          value: 0
        },
        iChannel0: { value: tex0 },
        iChannel1: { value: tex1 },
        iResolution: {
          type: 'v2',
          value: new Vector2(1200, 1200)
        },
        lineColor: {
          type: 'v4',
          value: new Vector4(bikeColor.r, bikeColor.g, bikeColor.b, 1)
        }
      },
      transparent: true,
      side: 2,
      vertexShader: `
        varying vec2 vUv;
        
        void main() {
          vUv=uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: `//////////////////////
        // Fire Flame shader
        
        // procedural noise from IQ
        
            uniform vec2 iResolution;
            uniform float iTime;
              uniform vec4 lineColor;
              varying vec2 vUv;
              // Neon bars with cats by TomCat/Abaddon
              uniform sampler2D iChannel0;
              uniform sampler2D iChannel1;

              #define Speed 1.5
              #define Amp .75
              #define PI 3.14159
              #define next PI*4.
              
              void main( )
              {
                vec2 fragCoord =1500. * vec2(vUv.y, vUv.x);
                vec4 fragColor = vec4(0);
                int Size = int(iResolution.y / 225.);
                  float Delta = 48. * float(Size) / iResolution.y;
              
                  float time = mod( Speed*iTime,next*2.5 );
                  if( (time+4.*Delta)>next*1.5 )
                  {
                  time = time + 4.*Delta;
                      Delta = 0.;
                  }
              
                  vec2 uv = vec2(1.-vUv.y,(vUv.x+0.2));				// screen coords: 0-1
                  vec3 color = vec3(texture( iChannel0, uv ).z*.5);		// background
              
                  float bar = cos(6.*(uv.y-.5));							// big bar
                  int b = 3 & int(8.*iTime);
                  float frame = float( b & ((((b&1)*2) ^ (b&2)) +1) );	// 0,1,2,1
                  vec2 cat = vec2(.45+(1.-uv.x)*.5-.15*frame-mod(.25*iTime,.15),uv.y*3.-1.05);
                  if(bar>.3)
                  {
                      if(texture( iChannel1, cat ).w > 0.) color = texture( iChannel1, cat ).xyz;
                      else color = vec3(bar,bar,bar);
                  }
              
                  vec3 ncolor = vec3(1., 0., 1.);							// magenta
                  float row = .5*(cos(time)*Amp + 1.) * iResolution.y + 16.*float(Size);
                  int dy = int(row - fragCoord.y);
                  if(dy>0 && dy<(32*Size+1))
                  {
                      if(dy>16*Size) dy = 32*Size - dy + 1;
                      float neon = float(dy) * 4. / float(Size);			// glow
                    color = color + neon/255. * ncolor;
                      if(dy>10*Size)
                      {
                        neon = float(dy-8*Size) * 20. / float(Size);	// tube
                        if(dy>(16-1)*Size) neon = 255.;					// core
                      color = neon/255. * ncolor;
                      }
                  }
                  
                  ncolor = vec3(1., 0., 0.);								// red
                  time = time + Delta;
                  row = .5*(cos(time)*Amp + 1.) * iResolution.y + 16.*float(Size);
                  dy = int(row - fragCoord.y);
                  if(dy>0 && dy<(32*Size+1))
                  {
                      if(dy>16*Size) dy = 32*Size - dy + 1;
                      float neon = float(dy) * 4. / float(Size);			// glow
                    color = color + neon/255. * ncolor;
                      if(dy>10*Size)
                      {
                        neon = float(dy-8*Size) * 20. / float(Size);	// tube
                        if(dy>(16-1)*Size) neon = 255.;					// core
                      color = neon/255. * ncolor;
                      }
                  }
              
                  ncolor = vec3(1., 1., 0.);								// yellow
                  time = time + Delta;
                  row = .5*(cos(time)*Amp + 1.) * iResolution.y + 16.*float(Size);
                  dy = int(row - fragCoord.y);
                  if(dy>0 && dy<(32*Size+1))
                  {
                      if(dy>16*Size) dy = 32*Size - dy + 1;
                      float neon = float(dy) * 4. / float(Size);			// glow
                    color = color + neon/255. * ncolor;
                      if(dy>10*Size)
                      {
                        neon = float(dy-8*Size) * 20. / float(Size);	// tube
                        if(dy>(16-1)*Size) neon = 255.;					// core
                      color = neon/255. * ncolor;
                      }
                  }
              
                  ncolor = vec3(0., 1., 0.);								// green
                  time = time + Delta;
                  row = .5*(cos(time)*Amp + 1.) * iResolution.y + 16.*float(Size);
                  dy = int(row - fragCoord.y);
                  if(dy>0 && dy<(32*Size+1))
                  {
                      if(dy>16*Size) dy = 32*Size - dy + 1;
                      float neon = float(dy) * 4. / float(Size);			// glow
                    color = color + neon/255. * ncolor;
                      if(dy>10*Size)
                      {
                        neon = float(dy-8*Size) * 20. / float(Size);	// tube
                        if(dy>(16-1)*Size) neon = 255.;					// core
                      color = neon/255. * ncolor;
                      }
                  }
                  
                  ncolor = vec3(0., 1., 1.);								// cyan
                  time = time + Delta;
                  row = .5*(cos(time)*Amp + 1.) * iResolution.y + 16.*float(Size);
                  dy = int(row - fragCoord.y);
                  if(dy>0 && dy<(32*Size+1))
                  {
                      if(dy>16*Size) dy = 32*Size - dy + 1;
                      float neon = float(dy) * 4. / float(Size);			// glow
                    color = color + neon/255. * ncolor;
                      if(dy>10*Size)
                      {
                        neon = float(dy-8*Size) * 20. / float(Size);	// tube
                        if(dy>(16-1)*Size) neon = 255.;					// core
                      color = neon/255. * ncolor;
                      }
                  }
              
                  if( ( 1& int( Speed*iTime/PI ) )==1) if(bar>.3)
                  {
                      if(texture( iChannel1, cat ).w > 0.) color = texture( iChannel1, cat ).xyz;
                      else color = vec3(bar,bar,bar);
                  }
                  
                fragColor = vec4(color, 1.0);
                gl_FragColor = fragColor;
              }`
    })
    return materials
  }, [bikeColor])

  const originalUvs = useMemo(() => {
    const uvs = [...nodes.Plane.geometry.attributes.uv.array]
    return new Float32Array(uvs)
  }, [])
  useFrame(({ clock }) => {
    material.uniforms.iTime.value = clock.elapsedTime
  })

  return (
    <group {...props}>
      <mesh ref={trailRef} {...nodes.Plane} material={material} />
    </group>
  )
}

export default CharTrail
